/**
 * FormErrorComponentComponent - Function Index
 * 
 * 1. shouldShowErrors - Getter that checks if the control should display errors
 * 2. errorMessages - Getter that retrieves all validation errors for the control
 * 3. getErrorMessage - Gets error message for a specific error key
 * 4. trackByIndex - Track by function for *ngFor optimization
 */

import { Component, Input } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormErrorKey } from './common/enums/form-error-component.enum';

/**
 * Global Form Error Component
 * 
 * A reusable component for displaying form validation errors in a consistent manner.
 * 
 * @example
 * <app-form-error-component 
 *   [control]="myForm.get('fieldName')"
 *   [customMessages]="{ [FormErrorKey.REQUIRED]: 'Please enter a value' }">
 * </app-form-error-component>
 */
@Component({
  standalone: true,
  selector: 'app-form-error-component',
  templateUrl: './form-error-component.component.html',
  styleUrls: ['./form-error-component.component.scss'],
  imports: [CommonModule],
})
export class FormErrorComponentComponent {
  /**
   * The form control to validate and display errors for
   */
  @Input() control: AbstractControl | null = null;

  /**
   * Custom error messages to override default messages
   * Key is the error name, value is the custom message
   */
  @Input() customMessages: { [key: string]: string } = {};

  /**
   * Whether to show the error icon
   * @default true
   */
  @Input() showIcon = true;

  /**
   * Whether to show only the first error or all errors
   * @default true
   */
  @Input() showFirstErrorOnly = true;

  /**
   * Custom CSS class to apply to error message container
   */
  @Input() customClass = '';

  /**
   * Path to custom error icon
   * @default 'assets/images/icon/error.png'
   */
  @Input() iconPath = 'assets/images/icon/error.png';

  /**
   * Default error messages for common validation errors
   */
  private readonly defaultErrorMessages: { [key: string]: (error: any) => string } = {
    [FormErrorKey.REQUIRED]: () => 'This field is required.',
    [FormErrorKey.MIN]: (error) => `Value must be greater than or equal to ${error.min}.`,
    [FormErrorKey.MAX]: (error) => `Value must be less than or equal to ${error.max}.`,
    [FormErrorKey.MIN_LENGTH]: (error) => `Minimum length is ${error.requiredLength} characters.`,
    [FormErrorKey.MAX_LENGTH]: (error) => `Maximum length is ${error.requiredLength} characters.`,
    [FormErrorKey.EMAIL]: () => 'Please enter a valid email address.',
    [FormErrorKey.INVALID_EMAIL]: () => 'Please enter a valid email address.',
    [FormErrorKey.PATTERN]: () => 'Invalid input format.',
    [FormErrorKey.GREATER_THAN_ZERO]: () => 'Value must be greater than 0.',
    [FormErrorKey.NONE_ZERO]: () => 'Value cannot be 0.',
    [FormErrorKey.FUTURE_DATE]: () => 'Invalid date.',
    [FormErrorKey.INVALID_DATE]: () => 'Please enter a valid date.',
    [FormErrorKey.DUPLICATE_DATA]: () => 'This value already exists.',
    [FormErrorKey.INVALID_SORT_CODE]: () => 'Invalid sort code.',
    [FormErrorKey.INVALID_IBAN]: () => 'Invalid IBAN.',
    [FormErrorKey.SPECIAL_CHARACTER_ERROR]: (error) => error.allowed || 'Invalid characters detected.',
    [FormErrorKey.BATCH_ALREADY_EXISTS]: () => 'This already exists.',
    [FormErrorKey.SWIFT_BIC]: () => 'Invalid Swift/BIC.',
    [FormErrorKey.INVALID_OPTION]: () => 'Selected value is not a valid option.',
  };

  /**
   * 1. shouldShowErrors - Getter that checks if the control should display errors
   * Check if the control should display errors
   * Validates that control exists, is invalid, and has been touched or modified
   * @returns True if errors should be displayed, false otherwise
   */
  get shouldShowErrors(): boolean {
    return !!(this.control && this.control.invalid && (this.control.touched || this.control.dirty));
  }

  /**
   * 2. errorMessages - Getter that retrieves all validation errors for the control
   * Get all validation errors for the control
   * Iterates through control errors and generates appropriate error messages
   * Returns either first error only or all errors based on showFirstErrorOnly setting
   * @returns Array of error message strings
   */
  get errorMessages(): string[] {
    if (!this.shouldShowErrors || !this.control?.errors) {
      return [];
    }

    const errors = this.control.errors;
    const messages: string[] = [];

    Object.keys(errors).forEach((errorKey) => {
      const message = this.getErrorMessage(errorKey, errors[errorKey]);
      if (message) {
        messages.push(message);
      }
    });

    return this.showFirstErrorOnly && messages.length > 0 ? [messages[0]] : messages;
  }

  /**
   * 3. getErrorMessage - Gets error message for a specific error key
   * Get error message for a specific error key
   * Checks custom messages first, then default messages, with fallback for unknown errors
   * @param errorKey - The validation error key
   * @param error - The error object containing error details
   * @returns The formatted error message string
   * @private
   */
  private getErrorMessage(errorKey: string, error: any): string {
    // Check for custom message first
    if (this.customMessages[errorKey]) {
      return this.customMessages[errorKey];
    }

    // Use default message if available
    const defaultMessageFn = this.defaultErrorMessages[errorKey];
    if (defaultMessageFn) {
      return defaultMessageFn(error);
    }

    // Fallback for unknown errors
    return `Validation error: ${errorKey}`;
  }

  /**
   * 4. trackByIndex - Track by function for *ngFor optimization
   * Track by function for *ngFor optimization
   * Returns the index to help Angular track items in the error messages list
   * @param index - The index of the current item in the iteration
   * @returns The index value for tracking
   */
  trackByIndex(index: number): number {
    return index;
  }
}
